import { useEnum } from "@/api/services/global/enum";
import { useGetNationalityList } from "@/api/services/global/master";
import { Checkbox } from "@/components/elements/Checkbox";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { Select } from "@/components/elements/Select";
import { CityInput } from "@/components/elements/CityInput";
import { AvatarUploadForm } from "@/features/talent/components/talent-form/AvatarUploadForm";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { black, divider } from "@/theme/colors";
import { FCC } from "@/types/common";
import { ERecruitmentStatus, TALENT_LOCATION, TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useWatch } from "react-hook-form";

type TalentFormBasicInfoProps = {};

export const TalentFormBasicInfo: FCC<TalentFormBasicInfoProps> = () => {
  const methods = useFormContext<TTalentForm>();
  const { control, setValue } = methods;
  const { data: genderOptions } = useEnum("EGender");
  const { nationalityOptions } = useGetNationalityList();
  const recruitmentStatus = useWatch({ name: "recruitmentStatus" });
  const isDraft = recruitmentStatus === ERecruitmentStatus.DRAFT;

  const location = useWatch({ name: "location" });
  const isInJapan = location === TALENT_LOCATION.LOCAL;
  const isRecommendedTalent = useWatch({ name: "isRecommendedTalent" });

  const locationInfo = useLocationInfo({
    methods,
    names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" },
  });

  useEffect(() => {
    if (location === TALENT_LOCATION.OVERSEA) {
      setValue("zipCode", "");
      setValue("prefecture", null);
      setValue("city", null);
      setValue("address", null);
    }
  }, [location, setValue]);

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      {/* Start Basic Information */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="head20Bold" color={black[30]}>
            基本情報
          </Typography>
        </Box>
        <Box ml="auto">
          <FormField control={control} name="isRecommendedTalent">
            <Checkbox
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  おすすめ人材に設定
                </Stack>
              }
              checked={!!isRecommendedTalent}
            />
          </FormField>
        </Box>
      </Stack>

      <Stack mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="fullName">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="birthday">
              <InputDatePicker placeholder="1990/06/25" minDate={dayjs("1870/01/01")} maxDate={dayjs().subtract(18, "year")} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="nickName">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="talentNumber">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="nationalityId" required={!isDraft}>
              <Select placeholder="選択してください" options={nationalityOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="gender" required={!isDraft}>
              <RadioGroup direction="row" options={genderOptions} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="phoneNumber">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="email">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="location" required={!isDraft}>
              <Select placeholder="選択してください" options={TALENT_LOCATION_OPTIONS} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="religion">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* End Basic Information */}

      {/* Start Location */}
      <Stack mt={3} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            現住所（国内）
          </Typography>
        </Box>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="zipCode" required={!isDraft && isInJapan}>
              <Input placeholder="入力してください" disabled={!isInJapan} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="prefecture">
              <Input placeholder="入力してください" readonly />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <CityInput name="city" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} disabled={!isInJapan} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="address" required={!isDraft && isInJapan}>
              <Input placeholder="入力してください" disabled={!isInJapan} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      {/* End Location */}

      {/* Start Residence */}
      <Stack mt={3} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            本国における居住地
          </Typography>
        </Box>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="country">
              <Input placeholder="入力してください" autoComplete="off" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      {/* End Residence */}

      {/* Upload profile */}
      <Stack mt={3} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            プロフィール写真のアップロード
          </Typography>
        </Box>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="avatarFile">
              <AvatarUploadForm />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* End Residence */}
    </InnerBox>
  );
};
